'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

import { financialDataService, type Commodity, type CityRate } from '@/lib/services/financialDataService';
import { FinancialAPIAdapter } from '@/lib/services/financialAPIAdapter';

// Commodity symbol mapping
const COMMODITIES = [
  { symbol: 'GC=F', name: 'Gold', icon: 'CubeIcon', unit: '$', id: 'gold', category: 'commodity' },
  { symbol: 'SI=F', name: 'Silver', icon: 'CircleStackIcon', unit: '$', id: 'silver', category: 'commodity' },
  { symbol: 'CL=F', name: 'Crude Oil', icon: 'BeakerIcon', unit: '$', id: 'oil', category: 'commodity' },
];

export default function CommodityRates() {
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [cityRates, setCityRates] = useState<CityRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'yahoofinance' | 'supabase'>('yahoofinance');

  const fetchFromAPI = async (): Promise<Commodity[] | null> => {
    try {
      const symbols = COMMODITIES.map((c) => c.symbol);
      
      // Try Finnhub first
      const adapter = new FinancialAPIAdapter('finnhub');
      let quotes = await adapter.getMultipleQuotes(symbols);
      
      // Fallback to Alpha Vantage if Finnhub fails
      if (quotes.size === 0) {
        console.log('Finnhub failed, trying Alpha Vantage...');
        adapter.setProvider('alphavantage');
        
        try {
          quotes = await adapter.getMultipleQuotes(symbols);
        } catch (alphaError: any) {
          console.error('Alpha Vantage error:', alphaError.message || alphaError);
          // Return null to trigger Supabase fallback
          return null;
        }
      }

      if (quotes.size === 0) {
        console.log('No quotes received from financial APIs');
        return null;
      }

      const commoditiesData: Commodity[] = [];
      COMMODITIES.forEach((commodity) => {
        const quote = quotes.get(commodity.symbol);
        if (quote) {
          commoditiesData.push({
            id: commodity.id,
            symbol: commodity.symbol,
            name: commodity.name,
            price: quote.price,
            change: quote.change,
            changePercent: quote.changePercent,
            unit: commodity.unit,
            icon: commodity.icon,
            category: commodity.category,
            updatedAt: new Date().toISOString(),
          });
        }
      });

      return commoditiesData.length > 0 ? commoditiesData : null;
    } catch (err: any) {
      console.error('Financial API error:', err.message || err);
      return null;
    }
  };

  const fetchFromSupabase = async (): Promise<Commodity[]> => {
    try {
      return await financialDataService.getCommodities();
    } catch (err) {
      console.error('Supabase fallback error:', err);
      return [];
    }
  };

  const fetchCityRatesFromSupabase = async (): Promise<CityRate[]> => {
    try {
      const rates = await financialDataService.getCityRates();
      return rates?.slice(0, 3) || [];
    } catch (err) {
      console.error('Supabase city rates error:', err);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Try Financial API first (Finnhub with Alpha Vantage fallback)
      const apiData = await fetchFromAPI();
      
      if (apiData && apiData.length > 0) {
        setCommodities(apiData);
        setDataSource('finnhub');
        setError(null);
      } else {
        // Fallback to Supabase
        console.log('Falling back to Supabase data');
        const supabaseData = await fetchFromSupabase();
        setCommodities(supabaseData);
        setDataSource('supabase');
        setError(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load commodity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 60 seconds
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 60000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading commodity rates...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-secondary">Error: {error}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="container mx-auto px-4 py-16 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-2 font-display text-[rgba(129,126,126,1)]">
            Commodity Rates
          </h2>
          <p className="text-muted-foreground">
            Live prices updated every 60 seconds {dataSource === 'yahoofinance' ? '(Yahoo Finance API)' : '(Supabase Fallback)'}
          </p>
        </div>
        <Link
          href="/markets"
          className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
        >
          Full Rates
          <Icon name="ArrowRightIcon" size={20} />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Commodity Cards */}
        <div className="space-y-4">
          {commodities?.map((commodity) => (
            <div
              key={commodity.id}
              className="bg-card border border-border rounded-xl p-6 flex items-center justify-between hover:border-accent transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                  <Icon
                    name={commodity.icon as any}
                    size={24}
                    className="text-accent"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">
                    {commodity.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">Per unit</p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-2xl font-bold text-foreground font-mono">
                  {commodity.unit}{commodity.price.toLocaleString('en-IN')}
                </p>
                <div
                  className={`flex items-center justify-end gap-1 text-sm font-mono ${
                    commodity.change >= 0 ? 'text-primary' : 'text-secondary'
                  }`}
                >
                  <Icon
                    name={commodity.change >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'}
                    size={16}
                    variant="solid"
                  />
                  <span>{commodity.unit}{Math.abs(commodity.change)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* City-wise Gold Rates */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
            <Icon name="MapPinIcon" size={24} className="text-accent" />
            City-wise Gold Rates
          </h3>
          <div className="space-y-4">
            {cityRates?.map((city) => (
              <div
                key={city.id}
                className="pb-4 border-b border-border last:border-0 last:pb-0"
              >
                <h4 className="text-base font-semibold text-foreground mb-3">
                  {city.city}
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground block mb-1">22K (10g)</span>
                    <span className="font-mono text-foreground font-semibold">
                      ₹{city.gold22k.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block mb-1">24K (10g)</span>
                    <span className="font-mono text-foreground font-semibold">
                      ₹{city.gold24k.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block mb-1">Silver (1kg)</span>
                    <span className="font-mono text-foreground font-semibold">
                      ₹{city.silver.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}